import React from 'react';
import DocLayout from '../DocLayout';
import { Search, Filter, SortAsc } from 'lucide-react';

const Filtering = () => {
  return (
    <DocLayout title="Lead Filtering and Search" category="Lead Management">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Filter className="w-6 h-6 text-indigo-600" />
            Finding Leads Quickly
          </h2>
          <p className="text-slate-700 mb-4">
            I-Match provides powerful search and filtering capabilities to help you find the right leads instantly.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Search className="w-5 h-5" />
            Search Functionality
          </h3>
          <p className="text-slate-700 mb-3">
            Use the search bar at the top of the leads page to search by:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Name</strong>: Find leads by contact name</li>
            <li><strong>Email</strong>: Search by email address</li>
            <li><strong>Company</strong>: Find all leads from a specific company</li>
            <li><strong>Phone</strong>: Search by phone number</li>
          </ul>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
            <p className="text-sm text-slate-700">
              <strong>Pro Tip:</strong> Search is real-time and case-insensitive. Start typing to see results immediately.
            </p>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Filter by Stage</h3>
          <p className="text-slate-700 mb-3">
            Use the stage dropdown to filter leads by pipeline stage:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="border rounded p-2 text-center">
              <span className="text-sm font-medium">New</span>
              <p className="text-xs text-slate-600">Recently added</p>
            </div>
            <div className="border rounded p-2 text-center">
              <span className="text-sm font-medium">Contacted</span>
              <p className="text-xs text-slate-600">First touch made</p>
            </div>
            <div className="border rounded p-2 text-center">
              <span className="text-sm font-medium">Qualified</span>
              <p className="text-xs text-slate-600">Meets criteria</p>
            </div>
            <div className="border rounded p-2 text-center">
              <span className="text-sm font-medium">Proposal</span>
              <p className="text-xs text-slate-600">Offer sent</p>
            </div>
            <div className="border rounded p-2 text-center">
              <span className="text-sm font-medium">Won</span>
              <p className="text-xs text-slate-600">Closed deals</p>
            </div>
            <div className="border rounded p-2 text-center">
              <span className="text-sm font-medium">Lost</span>
              <p className="text-xs text-slate-600">Unsuccessful</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Filter by Owner</h3>
          <p className="text-slate-700 mb-3">
            View leads assigned to specific team members:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li>Click the "Owner" filter dropdown</li>
            <li>Select a team member to see their leads</li>
            <li>Select "All" to view all leads</li>
            <li>Select "Unassigned" to find leads without owners</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Filter by Enrichment Status</h3>
          <p className="text-slate-700 mb-3">
            Quickly identify which leads have been AI-enriched:
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 border rounded">
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Enriched</span>
              <span className="text-sm text-slate-700">Leads with complete AI-generated data</span>
            </div>
            <div className="flex items-center gap-3 p-3 border rounded">
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded">Partial</span>
              <span className="text-sm text-slate-700">Leads with some enriched data</span>
            </div>
            <div className="flex items-center gap-3 p-3 border rounded">
              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">Not Enriched</span>
              <span className="text-sm text-slate-700">Leads without AI enrichment</span>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <SortAsc className="w-5 h-5" />
            Sorting Options
          </h3>
          <p className="text-slate-700 mb-3">
            Click on column headers to sort leads:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Name</strong>: Alphabetical order (A-Z or Z-A)</li>
            <li><strong>Company</strong>: Sort by company name</li>
            <li><strong>Stage</strong>: Group by pipeline stage</li>
            <li><strong>Score</strong>: Sort by AI fit score (highest to lowest)</li>
            <li><strong>Created Date</strong>: Most recent or oldest first</li>
            <li><strong>Updated Date</strong>: Recently modified leads</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Advanced Filtering</h3>
          <p className="text-slate-700 mb-3">
            Combine multiple filters for precise results:
          </p>
          <div className="bg-slate-50 border rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 mb-3">Example Filter Combinations:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-blue-100 rounded">Stage: Qualified</span>
                <span className="text-slate-400">+</span>
                <span className="px-2 py-1 bg-green-100 rounded">Owner: John</span>
                <span className="text-slate-400">=</span>
                <span className="text-slate-700">John's qualified leads</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-purple-100 rounded">Stage: New</span>
                <span className="text-slate-400">+</span>
                <span className="px-2 py-1 bg-yellow-100 rounded">Not Enriched</span>
                <span className="text-slate-400">=</span>
                <span className="text-slate-700">New leads needing enrichment</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-red-100 rounded">Stage: Proposal</span>
                <span className="text-slate-400">+</span>
                <span className="px-2 py-1 bg-indigo-100 rounded">Score: 80+</span>
                <span className="text-slate-400">=</span>
                <span className="text-slate-700">High-value opportunities</span>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Saved Filters (Coming Soon)</h3>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-slate-700 text-sm">
              In future updates, you'll be able to save your frequently-used filter combinations for quick access.
            </p>
          </div>
        </section>

        <section>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 mb-2">💡 Filtering Tips</h4>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Use search for quick lookups by name or email</li>
              <li>• Combine stage and owner filters to see team workload</li>
              <li>• Sort by score to prioritize high-value leads</li>
              <li>• Filter by "Not Enriched" to find leads needing AI data</li>
              <li>• Clear all filters to return to full list view</li>
            </ul>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default Filtering;